import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import api from '../api';

const STATUS_COLORS = {
  'planned': 'bg-gray-500/20 text-gray-400',
  'in-progress': 'bg-blue-500/20 text-blue-400',
  'done': 'bg-green-500/20 text-green-400',
  'on-hold': 'bg-amber-500/20 text-amber-400',
};

const PRIORITY_COLORS = {
  'critical': 'bg-destructive/20 text-destructive',
  'high': 'bg-amber-500/15 text-amber-500',
  'medium': 'bg-blue-500/15 text-blue-500',
  'low': 'bg-muted text-muted-foreground',
};

function PresentationView({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [roadmapData, itemsData] = await Promise.all([api.getRoadmap(id), api.getItems(id)]);
      setRoadmap(roadmapData); setItems(itemsData);
    } catch (error) { console.error('Failed to load data:', error); }
    finally { setLoading(false); }
  };

  const handleNext = () => { if (currentIndex < items.length - 1) setCurrentIndex(currentIndex + 1); };
  const handlePrev = () => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const handleKeyPress = (e) => {
    if (e.key === 'ArrowRight') handleNext();
    if (e.key === 'ArrowLeft') handlePrev();
    if (e.key === 'Escape') navigate(`/roadmap/${id}`);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, items]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-muted border-t-primary"></div>
    </div>
  );

  const currentItem = items[currentIndex];

  return (
    <div className="min-h-screen bg-background flex flex-col noise-overlay" data-testid="presentation-view">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-md border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/roadmap/${id}`)} className="p-2 hover:bg-secondary rounded-xl transition-colors" data-testid="exit-presentation">
            <X className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-semibold">{roadmap?.name}</h1>
            <p className="text-xs text-muted-foreground">{currentIndex + 1} of {items.length} items</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Progress bar */}
          <div className="hidden sm:flex items-center gap-1">
            {items.map((_, i) => (
              <button key={i} onClick={() => setCurrentIndex(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-primary w-6' : i < currentIndex ? 'bg-primary/40' : 'bg-muted'}`} />
            ))}
          </div>
          <span className="text-xs text-muted-foreground">ESC to exit &middot; Arrow keys to navigate</span>
        </div>
      </div>

      {/* Main Presentation Area */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 hero-sheen">
        {items.length === 0 ? (
          <div className="text-center text-muted-foreground">
            <p className="text-lg">No items to present</p>
          </div>
        ) : currentItem ? (
          <div className="max-w-4xl w-full relative z-10">
            <div className="bg-card border border-border rounded-2xl p-8 sm:p-14 shadow-2xl">
              <div className="flex items-start justify-between mb-8">
                <span className={`text-sm px-3 py-1.5 rounded-lg font-semibold capitalize badge-${currentItem.type}`}>{currentItem.type}</span>
                <span className={`text-sm px-3 py-1.5 rounded-lg font-medium capitalize ${STATUS_COLORS[currentItem.status] || ''}`}>{currentItem.status}</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-bold mb-6 leading-tight">{currentItem.title}</h2>
              {currentItem.description && (
                <p className="text-lg sm:text-xl text-muted-foreground mb-10 leading-relaxed">{currentItem.description}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-muted/50 rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Priority</p>
                  <p className={`text-sm font-semibold capitalize px-2 py-0.5 rounded-md w-fit ${PRIORITY_COLORS[currentItem.priority] || ''}`}>{currentItem.priority}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Timeframe</p>
                  <p className="text-sm font-semibold capitalize">{currentItem.timeframe}</p>
                </div>
                {currentItem.rice_score != null && (
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">RICE Score</p>
                    <p className="text-2xl font-bold font-mono-score text-primary">{currentItem.rice_score.toFixed(1)}</p>
                  </div>
                )}
                {currentItem.wsjf_score != null && (
                  <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">WSJF Score</p>
                    <p className="text-2xl font-bold font-mono-score text-primary">{currentItem.wsjf_score.toFixed(1)}</p>
                  </div>
                )}
                {currentItem.owner && (
                  <div className="p-4 bg-muted/50 rounded-xl border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Owner</p>
                    <p className="text-sm font-semibold">{currentItem.owner.split('@')[0]}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Navigation */}
      <div className="bg-card/80 backdrop-blur-md border-t border-border px-6 py-4 flex items-center justify-center gap-6">
        <button onClick={handlePrev} disabled={currentIndex === 0} className="p-3 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-testid="prev-slide">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-base font-semibold font-mono-score min-w-[80px] text-center">{currentIndex + 1} / {items.length}</span>
        <button onClick={handleNext} disabled={currentIndex === items.length - 1} className="p-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity" data-testid="next-slide">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

export default PresentationView;
